/**
 * The furniture every feed screen carries (spec 9.2): the category label with the topic title
 * top-left, the day-goal ring top-right, and the vertical dot rail on the right edge. Ported
 * from prototype A "Vollbild" at its exact sizes.
 */

import { useNavigate } from "react-router";
import { Button } from "../../shared/primitives/Button.js";

/** The prototype's ring: r=17 in a 40×40 box, so the full circumference is 2πr ≈ 106.8. */
const RING_CIRCUMFERENCE = 106.8;

export function GoalRing({ today, dayGoal }: { today: number; dayGoal: number }) {
  const navigate = useNavigate();
  const progress = Math.min(today / dayGoal, 1) * RING_CIRCUMFERENCE;
  return (
    <Button
      onClick={() => void navigate("/profil")}
      aria-label={"Profil öffnen. Heute " + today + " von " + dayGoal + " Themen"}
      className="absolute top-[14px] right-[18px] z-2 h-10 w-10"
    >
      <svg viewBox="0 0 40 40" className="h-10 w-10 -rotate-90" aria-hidden="true">
        <circle cx="20" cy="20" r="17" fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="4" />
        <circle
          cx="20"
          cy="20"
          r="17"
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="4"
          strokeDasharray={progress + " " + RING_CIRCUMFERENCE}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute inset-0 grid place-items-center text-[12px] font-bold text-white">
        {today}/{dayGoal}
      </span>
    </Button>
  );
}

export function TopicLabel({ category, title }: { category: string; title: string }) {
  return (
    <div className="absolute top-[18px] left-5 z-2 text-[12px] font-semibold tracking-[.1em] text-white uppercase opacity-80">
      {category}
      <b className="block text-[14px] tracking-normal normal-case opacity-100">{title}</b>
    </div>
  );
}

/**
 * Spec 9.2: one dot per card, read cards elongated white, the current one elongated yellow,
 * unread small and dim. Only the cards exist as dots; the gate is not one of them.
 */
export function CardRail({ total, current }: { total: number; current: number }) {
  return (
    <div
      className="absolute right-[18px] bottom-[60px] z-2 flex flex-col items-center gap-2"
      aria-hidden="true"
    >
      {Array.from({ length: total }, (_, index) => {
        const position = index + 1;
        const state =
          position < current
            ? "h-[18px] bg-white"
            : position === current
              ? "h-[18px] bg-[var(--color-accent)]"
              : "h-[6px] bg-white/35";
        return <i key={position} className={"block w-[6px] rounded-[3px] " + state} />;
      })}
    </div>
  );
}
